import {
    Editor,
    Point,
    Range,
} from 'slate';

import {
    IAnnotationTextAnchor,
} from '../interfaces';

interface ITextSegment {
    path: number[],
    pathKey: string,
    text: string,
    startOffset: number,
    endOffset: number,
}

interface IEditorTextIndex {
    segments: ITextSegment[],
    text: string,
    totalLength: number,
}

export interface IResolvedUpdatedAnnotationSelection {
    selection: Range,
    text: string,
}

const buildPathKey = (path: number[]): string => path.join('.');

/**
 * Flattens every text leaf into one global text index so moved annotations can
 * be recovered even when later edits split or merge Slate leaves.
 */
function collectTextSegments(
    nodes: unknown,
    parentPath: number[] = [],
    segments: ITextSegment[] = [],
    currentOffset = 0,
): {
    segments: ITextSegment[],
    currentOffset: number,
} {
    if (!Array.isArray(nodes)) {
        return {
            segments,
            currentOffset,
        };
    }

    let nextOffset = currentOffset;
    nodes.forEach((node, index) => {
        const nextPath = [
            ...parentPath,
            index,
        ];

        if (
            node != null
            && typeof node === 'object'
            && 'text' in node
            && typeof (node as { text?: unknown }).text === 'string'
        ) {
            const nextText = (node as { text: string }).text;
            if (nextText.length > 0) {
                segments.push({
                    path: nextPath,
                    pathKey: buildPathKey(nextPath),
                    text: nextText,
                    startOffset: nextOffset,
                    endOffset: nextOffset + nextText.length,
                });
            }
            nextOffset += nextText.length;
            return;
        }

        const nestedResult = collectTextSegments(
            (node as { children?: unknown })?.children,
            nextPath,
            segments,
            nextOffset,
        );
        nextOffset = nestedResult.currentOffset;
    });

    return {
        segments,
        currentOffset: nextOffset,
    };
}

/**
 * Builds one flattened view of the current editor text so selection recovery
 * can reason about repeated quote text across multiple leaves.
 */
function buildEditorTextIndex(editor: Editor): IEditorTextIndex {
    const {
        segments,
        currentOffset,
    } = collectTextSegments(editor.children);

    return {
        segments,
        text: segments.map((segment) => segment.text).join(''),
        totalLength: currentOffset,
    };
}

/**
 * Converts one global character offset back into a Slate point.
 */
function resolvePointAtGlobalOffset(
    scopeTextIndex: IEditorTextIndex,
    globalOffset: number,
    preferPreviousSegment = false,
): Point | null {
    if (!Number.isFinite(globalOffset)) return null;
    if (
        globalOffset < 0
        || globalOffset > scopeTextIndex.totalLength
    ) {
        return null;
    }

    for (let i = 0; i < scopeTextIndex.segments.length; i += 1) {
        const segment = scopeTextIndex.segments[i];
        if (
            preferPreviousSegment
            && globalOffset === segment.endOffset
        ) {
            return {
                path: segment.path,
                offset: segment.text.length,
            };
        }

        if (
            globalOffset > segment.startOffset
            && globalOffset < segment.endOffset
        ) {
            return {
                path: segment.path,
                offset: globalOffset - segment.startOffset,
            };
        }

        if (
            !preferPreviousSegment
            && globalOffset === segment.startOffset
        ) {
            return {
                path: segment.path,
                offset: 0,
            };
        }
    }

    const lastSegment = scopeTextIndex.segments[scopeTextIndex.segments.length - 1];
    if (
        lastSegment
        && globalOffset === scopeTextIndex.totalLength
    ) {
        return {
            path: lastSegment.path,
            offset: lastSegment.text.length,
        };
    }

    return null;
}

/**
 * Converts one exact text hit into a Slate selection.
 */
function buildSelectionFromGlobalOffsets(
    scopeTextIndex: IEditorTextIndex,
    startOffset: number,
    endOffset: number,
): Range | null {
    if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset)) return null;
    if (startOffset < 0 || endOffset < startOffset) return null;

    if (startOffset === endOffset) {
        const point = resolvePointAtGlobalOffset(scopeTextIndex, startOffset)
            || resolvePointAtGlobalOffset(scopeTextIndex, startOffset, true);
        if (!point) {
            return null;
        }

        return {
            anchor: point,
            focus: point,
        };
    }

    const anchor = resolvePointAtGlobalOffset(scopeTextIndex, startOffset);
    const focus = resolvePointAtGlobalOffset(scopeTextIndex, endOffset, true);
    if (!anchor || !focus) {
        return null;
    }

    return {
        anchor,
        focus,
    };
}

function findMatchOffsets(
    sourceText: string,
    targetText: string,
): number[] {
    if (
        typeof targetText !== 'string'
        || targetText.length === 0
    ) {
        return [];
    }

    const offsets: number[] = [];
    let fromIndex = 0;

    while (fromIndex <= sourceText.length) {
        const matchIndex = sourceText.indexOf(targetText, fromIndex);
        if (matchIndex === -1) break;

        offsets.push(matchIndex);
        fromIndex = matchIndex + 1;
    }

    return offsets;
}

function buildResolvedSelectionMatch(
    scopeTextIndex: IEditorTextIndex,
    startOffset: number,
    endOffset: number,
): IResolvedUpdatedAnnotationSelection | null {
    const selection = buildSelectionFromGlobalOffsets(
        scopeTextIndex,
        startOffset,
        endOffset,
    );
    if (!selection) {
        return null;
    }

    return {
        selection,
        text: scopeTextIndex.text.slice(startOffset, endOffset),
    };
}

/**
 * Finds every exact text match in the flattened editor text.
 */
function findSelectionMatchesByText(
    scopeTextIndex: IEditorTextIndex,
    expectedText: string,
): Range[] {
    if (typeof expectedText !== 'string' || expectedText.length === 0) {
        return [];
    }

    const matches: Range[] = [];
    let fromIndex = 0;

    while (fromIndex <= scopeTextIndex.text.length) {
        const matchIndex = scopeTextIndex.text.indexOf(expectedText, fromIndex);
        if (matchIndex === -1) break;

        const selection = buildSelectionFromGlobalOffsets(
            scopeTextIndex,
            matchIndex,
            matchIndex + expectedText.length,
        );
        if (selection) {
            matches.push(selection);
        }

        fromIndex = matchIndex + 1;
    }

    return matches;
}

function getGlobalOffsetForPoint(
    scopeTextIndex: IEditorTextIndex,
    point: Point,
): number | null {
    const segment = scopeTextIndex.segments.find((entry) => (
        entry.pathKey === buildPathKey(point.path)
    ));
    if (!segment) {
        return null;
    }
    if (
        !Number.isFinite(point.offset)
        || point.offset < 0
        || point.offset > segment.text.length
    ) {
        return null;
    }

    return segment.startOffset + point.offset;
}

function resolveSelectionGlobalOffsets(
    scopeTextIndex: IEditorTextIndex,
    selection: Range,
): {
    startOffset: number,
    endOffset: number,
} | null {
    const anchorOffset = getGlobalOffsetForPoint(scopeTextIndex, selection.anchor);
    const focusOffset = getGlobalOffsetForPoint(scopeTextIndex, selection.focus);
    if (
        anchorOffset == null
        || focusOffset == null
    ) {
        return null;
    }

    return anchorOffset <= focusOffset
        ? {
            startOffset: anchorOffset,
            endOffset: focusOffset,
        }
        : {
            startOffset: focusOffset,
            endOffset: anchorOffset,
        };
}

/**
 * Resolves one unique exact text match using saved prefix/suffix context to
 * disambiguate repeated quote text.
 */
function findSelectionByTextAnchorExactMatch(
    scopeTextIndex: IEditorTextIndex,
    textAnchor: IAnnotationTextAnchor | undefined,
    fallbackText: string,
): IResolvedUpdatedAnnotationSelection | null {
    const expectedText = typeof textAnchor?.text === 'string' && textAnchor.text.length > 0
        ? textAnchor.text
        : fallbackText;
    const matches = findSelectionMatchesByText(scopeTextIndex, expectedText);
    if (matches.length === 0) {
        return null;
    }
    if (matches.length === 1) {
        return {
            selection: matches[0],
            text: expectedText,
        };
    }

    const prefixText = typeof textAnchor?.prefixText === 'string'
        ? textAnchor.prefixText
        : '';
    const suffixText = typeof textAnchor?.suffixText === 'string'
        ? textAnchor.suffixText
        : '';
    if (!prefixText && !suffixText) {
        return null;
    }

    const rankedMatches = matches
        .map((selection) => {
            const offsets = resolveSelectionGlobalOffsets(scopeTextIndex, selection);
            if (!offsets) {
                return null;
            }

            const candidatePrefix = scopeTextIndex.text.slice(
                Math.max(0, offsets.startOffset - prefixText.length),
                offsets.startOffset,
            );
            const candidateSuffix = scopeTextIndex.text.slice(
                offsets.endOffset,
                offsets.endOffset + suffixText.length,
            );

            let score = 0;
            if (prefixText) {
                if (candidatePrefix !== prefixText) {
                    return null;
                }
                score += prefixText.length;
            }
            if (suffixText) {
                if (candidateSuffix !== suffixText) {
                    return null;
                }
                score += suffixText.length;
            }

            return {
                selection,
                score,
            };
        })
        .filter((match): match is { selection: Range, score: number } => !!match);

    if (rankedMatches.length === 1) {
        return {
            selection: rankedMatches[0].selection,
            text: expectedText,
        };
    }

    if (rankedMatches.length > 1) {
        rankedMatches.sort((firstMatch, secondMatch) => secondMatch.score - firstMatch.score);
        if (rankedMatches[0].score > rankedMatches[1].score) {
            return {
                selection: rankedMatches[0].selection,
                text: expectedText,
            };
        }
    }

    return null;
}

/**
 * Falls back to the saved before/after context when the quote text itself has
 * changed. This supports multi-character edits inside the quote without
 * hard-coding one-character shift rules.
 */
function findSelectionByTextAnchorContextRange(
    scopeTextIndex: IEditorTextIndex,
    textAnchor: IAnnotationTextAnchor | undefined,
): IResolvedUpdatedAnnotationSelection | null {
    const prefixText = typeof textAnchor?.prefixText === 'string'
        ? textAnchor.prefixText
        : '';
    const suffixText = typeof textAnchor?.suffixText === 'string'
        ? textAnchor.suffixText
        : '';

    if (!prefixText || !suffixText) {
        return null;
    }

    const prefixOffsets = findMatchOffsets(scopeTextIndex.text, prefixText);
    const suffixOffsets = findMatchOffsets(scopeTextIndex.text, suffixText);
    const candidates: IResolvedUpdatedAnnotationSelection[] = [];

    prefixOffsets.forEach((prefixOffset) => {
        const candidateStartOffset = prefixOffset + prefixText.length;
        suffixOffsets.forEach((suffixOffset) => {
            if (suffixOffset < candidateStartOffset) {
                return;
            }

            const candidate = buildResolvedSelectionMatch(
                scopeTextIndex,
                candidateStartOffset,
                suffixOffset,
            );
            if (candidate) {
                candidates.push(candidate);
            }
        });
    });

    if (candidates.length !== 1) {
        return null;
    }

    return candidates[0];
}

/**
 * Re-applies the stored/current scope delta to a recovered editor selection.
 * Post annotation normalization only shifts the first node index, so we keep
 * the rest of the recovered path intact and restore the original direction.
 */
function mapRecoveredSelectionToStoredCoordinateSpace({
    storedSelection,
    normalizedSelection,
    recoveredSelection,
}: {
    storedSelection: Range,
    normalizedSelection: Range,
    recoveredSelection: Range,
}): Range | null {
    const [recoveredStartPoint, recoveredEndPoint] = Range.edges(recoveredSelection);
    const anchorNodeOffset = storedSelection.anchor.path[0] - normalizedSelection.anchor.path[0];
    const focusNodeOffset = storedSelection.focus.path[0] - normalizedSelection.focus.path[0];

    const startPoint = {
        path: [
            recoveredStartPoint.path[0] + anchorNodeOffset,
            ...recoveredStartPoint.path.slice(1),
        ],
        offset: recoveredStartPoint.offset,
    };
    const endPoint = {
        path: [
            recoveredEndPoint.path[0] + focusNodeOffset,
            ...recoveredEndPoint.path.slice(1),
        ],
        offset: recoveredEndPoint.offset,
    };

    if (
        startPoint.path[0] < 0
        || endPoint.path[0] < 0
    ) {
        return null;
    }

    const storedSelectionIsForward = (
        Point.isBefore(storedSelection.anchor as Point, storedSelection.focus as Point)
        || Point.equals(storedSelection.anchor as Point, storedSelection.focus as Point)
    );

    return storedSelectionIsForward
        ? {
            anchor: startPoint,
            focus: endPoint,
        }
        : {
            anchor: endPoint,
            focus: startPoint,
        };
}

/**
 * Finds the current in-editor location of one annotation quote after the post
 * content moved, then converts that location back into the annotation's stored
 * coordinate space so PostEditor can persist the updated quote safely.
 */
function resolveUpdatedAnnotationSelection({
    editor,
    storedSelection,
    normalizedSelection,
    textAnchor,
    quoteText,
}: {
    editor: Editor,
    storedSelection: Range,
    normalizedSelection: Range,
    textAnchor?: IAnnotationTextAnchor,
    quoteText: string,
}): IResolvedUpdatedAnnotationSelection | null {
    const scopeTextIndex = buildEditorTextIndex(editor);
    const exactRecoveredSelection = findSelectionByTextAnchorExactMatch(
        scopeTextIndex,
        textAnchor,
        quoteText,
    );
    const contextRecoveredSelection = findSelectionByTextAnchorContextRange(
        scopeTextIndex,
        textAnchor,
    );
    const recoveredSelection = (
        contextRecoveredSelection
        && contextRecoveredSelection.text !== quoteText
    )
        ? contextRecoveredSelection
        : exactRecoveredSelection || contextRecoveredSelection;

    if (!recoveredSelection) {
        return null;
    }

    const mappedSelection = mapRecoveredSelectionToStoredCoordinateSpace({
        storedSelection,
        normalizedSelection,
        recoveredSelection: recoveredSelection.selection,
    });
    if (!mappedSelection) {
        return null;
    }

    return {
        selection: mappedSelection,
        text: recoveredSelection.text,
    };
}

export default resolveUpdatedAnnotationSelection;
